/**
 * Monthly / Quarterly / Yearly — the grid's period bucket.
 *
 * A segmented control rather than a select: three mutually exclusive options
 * whose current value has to be readable at a glance from the toolbar, next to
 * the numbers it describes. The aggregation happens server-side, so pressing a
 * segment is a scope change (a refetch), not a re-render.
 *
 * Drawn as the same grey track with a raised white segment as the Empty rows
 * control beside it (DOP-609), so the two read as one family of switches.
 */

import React from "react";

import { KPI_PERIOD_GRAINS, type KpiPeriodGrain } from "../../types/kpi";

export interface KpiGrainToggleProps {
	grain: KpiPeriodGrain;
	onChange: (grain: KpiPeriodGrain) => void;
	/** Disabled while a refetch is in flight, so a rapid click cannot queue. */
	disabled?: boolean;
}

const LABELS: Record<KpiPeriodGrain, string> = {
	month: "Monthly",
	quarter: "Quarterly",
	year: "Yearly",
};

const KpiGrainToggle: React.FC<KpiGrainToggleProps> = ({
	grain,
	onChange,
	disabled = false,
}) => (
	<div
		className="inline-flex gap-0.5 rounded-lg bg-[#f2f2f2] p-[3px]"
		role="group"
		aria-label="Period grain"
	>
		{KPI_PERIOD_GRAINS.map((option) => {
			const active = option === grain;
			return (
				<button
					key={option}
					type="button"
					aria-pressed={active}
					disabled={disabled}
					onClick={() => onChange(option)}
					className={`h-7 whitespace-nowrap rounded-md px-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#008782] disabled:opacity-50 ${
						active
							? "bg-white font-semibold text-[#111111] shadow-[0_1px_2px_rgba(17,24,39,0.14)]"
							: "text-text-muted hover:text-text-base"
					}`}
				>
					{LABELS[option]}
				</button>
			);
		})}
	</div>
);

export default React.memo(KpiGrainToggle);
